<template>
  <div
    :id="labelId"
    data-samora
    class="sam-label__wrapper"
    v-on="$listeners"
  >
    <div
      data-testid="sam-label__content"
      :class="[
        `sam-label is-${samStyle}`,
        { 'is-subtle': subtle && !loading },
        { 'is-loading': loading },
        { 'is-icon': isIconOnly },
        { 'hide-value': showOnHover }
      ]">
      <SamIcon
        v-if="icon"
        class="sam-label__icon"
        :icon="icon"
        :size="12"
        v-bind="iconProps"
      />
      <span
        v-if="value"
        class="sam-label__text"
      >
        {{ value }}
      </span>
    </div>

    <SamTooltip
      v-if="hasTooltip"
      :message="tooltipValue"
      :position="'top'"
      :anchor-name="labelId"
    >
    </SamTooltip>
  </div>
</template>

<script>
import SamTooltip from '../SamTooltip/SamTooltip.vue';
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import { randomizeId } from '../../../utils/utils';
import SamIcon from '../SamIcon/SamIcon.vue';

export const LABEL_STYLES = {
  PRIMARY: SAMORA_STYLES.PRIMARY,
  SECONDARY: SAMORA_STYLES.SECONDARY,
  WARNING: SAMORA_STYLES.WARNING,
  DANGER: SAMORA_STYLES.DANGER,
  SUCCESS: SAMORA_STYLES.SUCCESS,
  INACTIVE: SAMORA_STYLES.INACTIVE,
};

export default {
  name: 'SamLabel',
  components: {
    SamIcon,
    SamTooltip,
  },
  props: {
    /**
     * If the label renders in subtle type
     * @values true, false
     */
    subtle: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Label style (colors)
     * @values primary, secondary, warning, success, danger, inactive
     */
    samStyle: {
      type: String,
      required: false,
      default: 'primary',
      validator(type) {
        return Object.values(LABEL_STYLES).includes(type);
      },
    },
    /**
     * the Label value
     */
    value: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * the Label's tooltip value
     */
    tooltipValue: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Whether to set the label in a loading state
     * @values true, false
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The icon to prepend the label with
     */
    icon: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * The icon properties.
     *
     * Only will be applied if `icon` is set
     */
    iconProps: {
      type: Object,
      required: false,
      default: null,
    },
    /**
     * Whether to show value on label hover
     */
    showOnHover: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      labelId: `label__${randomizeId('labelid')}`,
    };
  },
  computed: {
    hasTooltip() {
      return this.tooltipValue !== '';
    },
    isIconOnly(){
      if(this.showOnHover) return true;

      return !!this.icon && !this.value;
    }
  },
};
</script>

<style lang="scss" scoped>
.sam-label {
  @extend .text--body-3, .d-inline-flex, .flex-center, .gap-1, .px-2;
  border-radius: $size--6;
  border: $size--0--fourth solid transparent;
  color: $primary--white;
  white-space: nowrap;


  &__wrapper {
    .hide-value > .sam-label__text {
      display: none;
    }


    &:hover {
      .hide-value > .sam-label__text {
        @extend .mr-1;
        display: block;
      }
    }
  }

  &__text {
    @extend .text--caption--demi, .m-0;
    max-width: 196px;
    border-radius: $size--1;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;

    animation: fadeInAnimation ease 1s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;

    &::first-letter {
      text-transform: uppercase;
    }

    @keyframes fadeInAnimation {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  }

  &.is-icon {
    @extend .px-1;
    min-height: 22px;
  }

  &.is-primary {
    background-color: $informative--main;
    border-color: $informative--main;

    &.is-subtle {
      color: $informative--main;
      background-color: $informative--surface;
      border-color: $informative--border;
    }
  }

  &.is-secondary {
    background-color: $info-secondary--main;
    border-color: $info-secondary--main;

    &.is-subtle {
      color: $info-secondary--main;
      background-color: $info-secondary--surface;
      border-color: $info-secondary--border;
    }
  }

  &.is-warning {
    background-color: $secondary--burning-orange;
    border-color: $secondary--burning-orange;

    &.is-subtle {
      color: $secondary--burning-orange;
      background-color: $warning--surface;
      border-color: $warning--border;
    }
  }

  &.is-danger {
    background-color: $error--main;
    border-color: $error--main;

    &.is-subtle {
      color: $error--hover;
      background-color: $error--surface;
      border-color: $error--border;
    }
  }

  &.is-success {
    background-color: $success--main;
    border-color: $success--main;

    &.is-subtle {
      border-color: $success--border;
      color: $success--main;
      background-color: $success--surface;
    }
  }

  &.is-inactive {
    background-color: $neutral--30 !important;
    border-color: $neutral--30 !important;
    color: $neutral--60 !important;
  }

  &.is-loading {
    display: block;
    border-color: transparent;
    height: $size--4;
    width: 75%;
    @include old-skeleton-blink;
    > * {
      display: none;
    }
  }
}
</style>
